// Shared by Communications and the staging GroupMe lineup-publishing API.
// Keep this formatter pure so a preview can be recomputed from saved rows.
const clean = (value) => String(value || '').trim();

function dateLabel(value, withWeekday = false) {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return String(value);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: withWeekday ? 'short' : undefined,
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function timeLabel(value, format = '12h') {
  const [hourText, minuteText] = String(value || '').slice(0, 5).split(':');
  if (!hourText || !minuteText) return '';
  const hours = Number(hourText);
  const minutes = Number(minuteText);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return String(value).slice(0, 5);
  if (format === '24h') return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
}

function lineStatusLabel(value) {
  if (value === 'default_by_opponent') return 'Default by Opponent';
  if (value === 'default_by_us') return 'Default by Us';
  return 'Normal';
}

export function formatInternalLineupMessage({ match, teamName, facility, positions, entries, statuses, players, overrideFacilities, timeFormat = '12h' }) {
  const statusByPosition = new Map((statuses || []).map((row) => [row.league_configuration_position_id, row]));
  const playerById = new Map((players || []).map((row) => [row.id, row]));
  const facilityById = new Map((overrideFacilities || []).map((row) => [row.id, row.facility_name]));
  const entriesByPosition = new Map();
  for (const entry of entries || []) {
    const bucket = entriesByPosition.get(entry.league_configuration_position_id) || [];
    bucket.push(entry);
    entriesByPosition.set(entry.league_configuration_position_id, bucket);
  }
  const date = dateLabel(match.match_date);
  const matchTime = timeLabel(match.match_time, timeFormat);
  const warmupTime = timeLabel(match.warmup_time, timeFormat);
  const header = `Here is our lineup for ${match.opponent_name || 'TBD'} at ${facility?.facility_name || 'the facility'} on ${date}${matchTime ? ` at ${matchTime}` : ''}.`;
  const firstLine = [header, warmupTime ? `Please arrive by ${warmupTime} for warmup.` : '', facility?.google_maps_url ? `(${facility.google_maps_url})` : ''].filter(Boolean).join(' ');
  const splitSchedule = (statuses || []).some((status) => Boolean(status.scheduled_date_override || status.warmup_time_override || status.start_time_override || status.facility_id_override || status.location_text_override || status.court_assignment));
  const lines = [firstLine, ''];
  for (const position of positions || []) {
    const status = statusByPosition.get(position.id) || {};
    if (status.line_status && status.line_status !== 'normal') {
      lines.push(`${position.position_code}: ${lineStatusLabel(status.line_status)}`);
      continue;
    }
    const names = (entriesByPosition.get(position.id) || [])
      .sort((a, b) => Number(a.slot_number) - Number(b.slot_number))
      .map((entry) => {
        const player = playerById.get(entry.player_id);
        return player ? [player.first_name, player.last_name].map(clean).filter(Boolean).join(' ') : '';
      }).filter(Boolean).join(' / ');
    const court = clean(status.court_assignment) || clean(match.court_numbers);
    const detail = splitSchedule
      ? [
          dateLabel(status.scheduled_date_override || match.match_date),
          status.warmup_time_override || match.warmup_time ? `Warmup ${timeLabel(status.warmup_time_override || match.warmup_time, timeFormat)}` : '',
          timeLabel(status.start_time_override || match.match_time, timeFormat),
          status.facility_id_override && status.facility_id_override !== match.facility_id ? facilityById.get(status.facility_id_override) || '' : clean(status.location_text),
          court ? `Court ${court}` : '',
        ].filter(Boolean).join(' · ')
      : court ? `Court ${court}` : '';
    lines.push(`${position.position_code}: ${names || 'TBD'}${detail ? ` — ${detail}` : ''}`);
  }
  return lines.join('\n');
}

export function normalizeLineupMessage(value) {
  return String(value || '').replace(/\r\n?/g, '\n').normalize('NFC').trim();
}
